import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import {
  GetApiV1ConversationsMessagesFeedbackreportParams,
  PatchApiV1ConversationsMessagesFeedbackParams
} from 'src/api/chatbot/generated/models';

import { Conversation, NewConversation } from './models';
// TODO: replace Conversation type with generated types
// import {
//   GetApiV1ConversationsQueryError,
//   GetApiV1ConversationsQueryResult
// } from './chatbot/generated/conversations/conversations';

type requiredHeaders = {
  token: string;
  memberid: string;
};

type conversationOptions = requiredHeaders & {
  payload: Conversation | NewConversation | null; // TODO: replace with generated type "ConversationPayload"
  abortSignal: AbortSignal;
  onmessage: (ev: EventSourceMessage) => void;
  onopen?: (response: Response) => Promise<void>;
  onclose?: () => void;
  onerror?: (err: any) => number | null | undefined | void;
};

export const conversationApi = async (options: conversationOptions) => {
  await fetchEventSource(`${import.meta.env.VITE_CHATBOT_URI}/conversations/stream`, {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Ocp-Apim-Subscription-Key': `${import.meta.env.VITE_CHATBOT_SUBSCRIPTION_KEY}`,
      'Content-Type': 'application/json',
      Connection: 'keep-alive',
      Authorization: `Bearer ${options.token}`,
      memberid: options.memberid
    },
    body: JSON.stringify(options.payload), // Your POST data
    signal: options.abortSignal,
    async onopen(response) {
      if (response.ok && response.status === 200) {
        console.log('Connection made');
      } else if (response.status >= 400 && response.status < 500) {
        throw new Error('Client side error');
      } else if (response.status >= 500 && response.status < 600) {
        throw new Error('Server side error');
      }
      if (options.onopen) options.onopen(response);
    },
    onmessage(event) {
      try {
        options.onmessage(event);
      } catch (error) {
        console.error('Error parsing SSE data:', error);
        if (options.onerror) options.onerror(error);
      }
    },
    onerror(error) {
      console.error('SSE connection error:', error);
      if (options.onerror) options.onerror(error);
    },
    onclose() {
      console.log('SSE connection closed');
      if (options.onclose) {
        options.onclose();
      }
    }
  });
};

type messageFeedbackReportOptions = requiredHeaders &
  PatchApiV1ConversationsMessagesFeedbackParams & {
    onerror: (err: Error | unknown) => void;
  };

export const messageFeedback = async (options: messageFeedbackReportOptions) => {
  try {
    let url = new URL(`${import.meta.env.VITE_CHATBOT_URI}/conversations/messages/feedback`);
    url.searchParams.append('messageId', options.messageId ?? '');
    url.searchParams.append('feedback', options.feedback ?? '');
    await fetch(url, {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        // 'Ocp-Apim-Subscription-Key': 'd00dc7209d1e430bad35849a0f4cdb69',
        // 'Content-Type': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        Connection: 'keep-alive',
        Authorization: `Bearer ${options.token}`,
        memberid: options.memberid
      }
    });
  } catch (ex) {
    console.error('ex:', ex);
  }
};

type downloadAllFeedbackReportOptions = requiredHeaders &
  GetApiV1ConversationsMessagesFeedbackreportParams & {
    onerror: (err: Error | unknown) => void;
  };

export const downloadAllFeedbackReport = async (options: downloadAllFeedbackReportOptions) => {
  try {
    let url = new URL(`${import.meta.env.VITE_CHATBOT_URI}/conversations/messages/feedbackreport`);
    url.searchParams.append('fromDate', options.fromDate ?? '');
    url.searchParams.append('toDate', options.toDate ?? '');
    return await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        Connection: 'keep-alive',
        Authorization: `Bearer ${options.memberid}`,
        memberid: options.token
      }
    })
      .then((res) => {
        if (!res.ok) {
          throw Error('Unexpected response from server');
        }
        return res;
      })
      .then((res) => res.blob())
      .then((blob) => {
        var file = window.URL.createObjectURL(blob);
        window.location.assign(file);
      });
  } catch (error) {
    console.error('ex:', error);
    options.onerror(error);
  }
};

type getAllConversationsOptions = requiredHeaders & {
  onerror: (err: Error | unknown) => void;
};

export const getAllConversations = async (options: getAllConversationsOptions) => {
  try {
    let url = new URL(`${import.meta.env.VITE_CHATBOT_URI}/conversations`);
    return await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        Connection: 'keep-alive',
        Authorization: `Bearer ${options.memberid}`,
        memberid: options.token
      }
    });
  } catch (error) {
    console.error('ex:', error);
    options.onerror(error);
  }
};
